import { useState, useEffect, useRef } from 'react';

export default function Hero() {
  const [counts, setCounts] = useState({ years: 0, clients: 0, success: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          animateCounters();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = { years: 15, clients: 500, success: 98 };
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        years: Math.floor(targets.years * progress),
        clients: Math.floor(targets.clients * progress),
        success: Math.floor(targets.success * progress),
      });

      if (currentStep >= steps) {
        setCounts(targets);
        clearInterval(timer);
      }
    }, interval);
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="home" 
      className="relative pt-20 min-h-screen flex items-center overflow-hidden"
    >
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="https://public.readdy.ai/ai/video_res/0e1adc1e-67a6-45b0-8d9d-fcaedf6ca2a2.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/60"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 lg:py-20 w-full relative z-10">
        <div className="max-w-2xl lg:max-w-4xl">
          <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4 lg:mb-6">
            Strategic Business Solutions for Global Success
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 mb-6 lg:mb-8 leading-relaxed">
            Auralis Consulting delivers expert corporate strategy, tax optimization, international business setup, and comprehensive IT network management solutions tailored to elevate your business performance and drive sustainable growth in competitive markets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={scrollToContact}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all font-semibold text-lg cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              Schedule Consultation
            </button>
            <button 
              onClick={() => {
                const element = document.getElementById('services');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg hover:bg-blue-50 transition-all font-semibold text-lg cursor-pointer whitespace-nowrap"
            >
              Explore Services
            </button>
          </div>
          <div ref={statsRef} className="mt-8 lg:mt-12 grid grid-cols-3 gap-6 lg:gap-8">
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                {counts.years}+
              </div>
              <div className="text-sm lg:text-base text-gray-600 font-medium">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                {counts.clients}+
              </div>
              <div className="text-sm lg:text-base text-gray-600 font-medium">Clients Served</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                {counts.success}%
              </div>
              <div className="text-sm lg:text-base text-gray-600 font-medium">Success Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}